import { useState } from "react";

import { PrintButton } from "@/Common/Components/Buttons/PrintButton";
import { RefreshButton } from "@/Common/Components/Buttons/RefreshButton";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { ExportCsvButton } from "@/Common/Components/Tables/ExportCsvButton";
import { SettingsButton } from "../Settings/SettingsButton";

import styles from "./CommonTableHeaderComponent.module.scss";

interface ICommonTableHeaderComponentProps {
    title: string;

    searchText: string;
    searchPlaceholder: string;
    displaySearchIcon?: boolean;
    enableSearchBoxAutoFocus?: boolean;
    onSearch?: (text: string) => void;

    onPrintClick?: () => void | Promise<void>;
    onSettingsClick?: () => void;
    onRefreshClick?: () => void;
    onExportCsvClick?: () => void;

    additionalLeftComponent?: JSX.Element[];
    additionalRightComponent?: JSX.Element[];

    hideRightSideComponents?: boolean;
    leftSideFlexGrow?: boolean;
}

function CommonTableHeaderComponent(props: ICommonTableHeaderComponentProps): JSX.Element {
    const [isPrintButtonDisabled, setIsPrintButtonDisabled] = useState(false);

    const renderTextboxControl = (): JSX.Element => (
        <>
            <TextboxControl
                debounceMs={400}
                value={props.searchText}
                className={`form-control primary-search ${props.displaySearchIcon ? styles.searchIconTextbox : styles.textbox}`}
                dataCy="common-table-search"
                placeholder={props.searchPlaceholder}
                onChange={(text: string) => props.onSearch(text)}
                focus={props.enableSearchBoxAutoFocus}
            />
        </>
    );

    const renderSearchIconTextboxControl = (): JSX.Element => (
        <>
            <div className="search-field">
                <div className="gf">
                    <span className="glyphicon glyphicon-search" />
                </div>
                {renderTextboxControl()}
            </div>
        </>
    );

    return (
        <div className={`myle-page-header ${styles.commonTableHeader}`}>

            <div
                role="commonTableHeaderLeftSide"
                className={`${styles.commonTableHeaderLeftSide} ${props.leftSideFlexGrow ? styles.commonTableHeaderLeftSideGrow : ""}`}
            >
                <h3 className={styles.commonTableHeaderThinFont}>
                    {props.title}
                </h3>

                {props.additionalLeftComponent}
            </div>

            {!props.hideRightSideComponents && (
                <div role="commonTableHeaderRightSide" className={styles.commonTableHeaderRightSide}>
                    {props.additionalRightComponent}

                    <div role="commonTableHeaderStandardButtons" className={styles.commonTableHeaderStandardButtons}>
                        {props.onExportCsvClick && (
                            <ExportCsvButton onClick={() => props.onExportCsvClick()} />
                        )}
                        {props.onPrintClick && (
                            <PrintButton
                                onClickCallback={async () => {
                                    setIsPrintButtonDisabled(true);
                                    await props.onPrintClick();
                                    setIsPrintButtonDisabled(false);
                                }}
                                rightMargin={0}
                                isDisabled={isPrintButtonDisabled}
                                dataCy="common-table-print"
                            />
                        )}
                        <RefreshButton onClick={() => props.onRefreshClick()} />
                        <SettingsButton onClick={() => props.onSettingsClick()} />
                    </div>
                    {props.displaySearchIcon ? renderSearchIconTextboxControl() : renderTextboxControl()}
                </div>
            )}
        </div>
    );
}
export { CommonTableHeaderComponent };
